import React, { useState } from 'react';
import { FaKey, FaCode, FaCopy, FaCheck, FaArrowLeft, FaDownload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Container from './styles';

const ApiDocumentation = () => {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState('');
  const [activeLanguage, setActiveLanguage] = useState('javascript');
  const [activeTab, setActiveTab] = useState('api'); // 'api' or 'embed'

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Code copied to clipboard!');
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const downloadPostmanCollection = () => {
    const collection = {
      info: {
        name: "Astar Public API",
        _postman_id: "astar-api-collection",
        description: "Complete API collection for Astar Logistics delivery service integration",
        schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
      },
      auth: {
        type: "apikey",
        apikey: [
          {
            key: "value",
            value: "{{api_key}}",
            type: "string"
          },
          {
            key: "key",
            value: "X-API-Key",
            type: "string"
          }
        ]
      },
      variable: [
        {
          key: "base_url",
          value: "https://api.astar.ng/api.php?path=/api/v1/public/v1",
          type: "string"
        },
        {
          key: "api_key",
          value: "your_api_key_here",
          type: "string"
        }
      ],
      item: [
        {
          name: "Orders",
          item: [
            {
              name: "Create Order",
              request: {
                method: "POST",
                header: [{ key: "Content-Type", value: "application/json" }],
                body: {
                  mode: "raw",
                  raw: JSON.stringify({
                    customerName: "John Doe",
                    customerPhone: "+234 XXX XXX XXXX",
                    customerEmail: "john@example.com",
                    pickupAddress: "123 Pickup Street, Lagos",
                    pickupLat: 6.5244,
                    pickupLng: 3.3792,
                    deliveryAddress: "456 Delivery Avenue, Lagos",
                    deliveryLat: 6.4281,
                    deliveryLng: 3.4219,
                    packageDescription: "Electronics - Handle with care",
                    packageWeight: "2.5kg",
                    packageValue: "₦50,000",
                    deliveryFee: 2500,
                    deliveryInstructions: "Call customer upon arrival"
                  }, null, 2)
                },
                url: {
                  raw: "{{base_url}}/orders",
                  host: ["{{base_url}}"],
                  path: ["orders"]
                },
                description: "Create a new delivery order"
              }
            },
            {
              name: "Get Order by Tracking Number",
              request: {
                method: "GET",
                header: [],
                url: {
                  raw: "{{base_url}}/orders/:trackingNumber",
                  host: ["{{base_url}}"],
                  path: ["orders", ":trackingNumber"],
                  variable: [
                    {
                      key: "trackingNumber",
                      value: "PL1234567890ABCD",
                      description: "The tracking number of the order"
                    }
                  ]
                },
                description: "Get order status by tracking number"
              }
            },
            {
              name: "Get All Orders (Paginated)",
              request: {
                method: "GET",
                header: [],
                url: {
                  raw: "{{base_url}}/orders?page=1&limit=20",
                  host: ["{{base_url}}"],
                  path: ["orders"],
                  query: [
                    { key: "page", value: "1", description: "Page number" },
                    { key: "limit", value: "20", description: "Items per page" }
                  ]
                },
                description: "Get paginated list of orders"
              }
            }
          ]
        }
      ]
    };

    const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Astar_Public_API.postman_collection.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Postman collection downloaded!');
  };

  const codeExamples = {
    javascript: {
      createOrder: `const axios = require('axios');

const apiKey = 'your_api_key_here';
const baseURL = 'https://api.astar.ng/api.php?path=/api/v1/public/v1';

async function createOrder() {
  try {
    const response = await axios.post(
      \`\${baseURL}/orders\`,
      {
        customerName: 'John Doe',
        customerPhone: '+234 XXX XXX XXXX',
        customerEmail: 'john@example.com',
        pickupAddress: '123 Pickup Street, Lagos',
        deliveryAddress: '456 Delivery Avenue, Lagos',
        packageDescription: 'Electronics',
        packageWeight: '2.5kg'
      },
      {
        headers: {
          'X-API-Key': apiKey,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Order created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error:', error.response.data);
  }
}`,
      getStatus: `async function getOrderStatus(trackingNumber) {
  try {
    const response = await axios.get(
      \`\${baseURL}/orders/\${trackingNumber}\`,
      {
        headers: {
          'X-API-Key': apiKey
        }
      }
    );
    
    console.log('Order status:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error:', error.response.data);
  }
}`
    },
    php: {
      createOrder: `<?php
$apiKey = 'your_api_key_here';
$baseURL = 'https://api.astar.ng/api.php?path=/api/v1/public/v1';

function createOrder($apiKey, $baseURL) {
    $data = array(
        'customerName' => 'John Doe',
        'customerPhone' => '+234 XXX XXX XXXX',
        'customerEmail' => 'john@example.com',
        'pickupAddress' => '123 Pickup Street, Lagos',
        'deliveryAddress' => '456 Delivery Avenue, Lagos',
        'packageDescription' => 'Electronics',
        'packageWeight' => '2.5kg'
    );
    
    $ch = curl_init("$baseURL/orders");
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
    curl_setopt($ch, CURLOPT_HTTPHEADER, array(
        'X-API-Key: ' . $apiKey,
        'Content-Type: application/json'
    ));
    
    $response = curl_exec($ch);
    curl_close($ch);
    
    return json_decode($response, true);
}
?>`,
      getStatus: `<?php
function getOrderStatus($apiKey, $baseURL, $trackingNumber) {
    $ch = curl_init("$baseURL/orders/$trackingNumber");
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_HTTPHEADER, array(
        'X-API-Key: ' . $apiKey
    ));
    
    $response = curl_exec($ch);
    curl_close($ch);
    
    return json_decode($response, true);
}
?>`
    },
    python: {
      createOrder: `import requests

api_key = 'your_api_key_here'
base_url = 'https://api.astar.ng/api.php?path=/api/v1/public/v1'

def create_order():
    headers = {
        'X-API-Key': api_key,
        'Content-Type': 'application/json'
    }
    
    data = {
        'customerName': 'John Doe',
        'customerPhone': '+234 XXX XXX XXXX',
        'customerEmail': 'john@example.com',
        'pickupAddress': '123 Pickup Street, Lagos',
        'deliveryAddress': '456 Delivery Avenue, Lagos',
        'packageDescription': 'Electronics',
        'packageWeight': '2.5kg'
    }
    
    response = requests.post(f'{base_url}/orders', json=data, headers=headers)
    return response.json()`,
      getStatus: `def get_order_status(tracking_number):
    headers = {
        'X-API-Key': api_key
    }
    
    response = requests.get(f'{base_url}/orders/{tracking_number}', headers=headers)
    return response.json()`
    }
  };

  return (
    <Container>
      <div className="doc--header">
        <button className="back--button" onClick={() => navigate('/company/dashboard/settings')}>
          <FaArrowLeft />
          Back to Settings
        </button>
        <h1>Integration Documentation</h1>
        <p>Complete guide to integrate Astar into your application</p>
        
        {activeTab === 'api' && (
          <button className="download--button" onClick={downloadPostmanCollection}>
            <FaDownload />
            Download Postman Collection
          </button>
        )}
      </div>

      {/* Main Tabs */}
      <div className="main--tabs">
        <button 
          className={`main--tab ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          REST API
        </button>
        <button 
          className={`main--tab ${activeTab === 'embed' ? 'active' : ''}`}
          onClick={() => setActiveTab('embed')}
        >
          Embed Tracking Widget
        </button>
      </div>

      {activeTab === 'api' ? (
        <div className="doc--content">
        {/* Getting Started */}
        <section className="doc--section">
          <h2>🚀 Getting Started</h2>
          <p>
            The Astar API allows you to create delivery orders, track shipments, 
            and manage logistics programmatically. All requests require authentication using an API key.
          </p>
        </section>

        {/* Base URL */}
        <section className="doc--section">
          <h2>🌐 Base URL</h2>
          <div className="code--block">
            <code>https://api.astar.ng/api.php?path=/api/v1/public/v1</code>
            <button 
              className="copy--btn"
              onClick={() => copyToClipboard('https://api.astar.ng/api.php?path=/api/v1/public/v1', 'baseurl')}
            >
              {copiedCode === 'baseurl' ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
        </section>

        {/* Authentication */}
        <section className="doc--section">
          <h2><FaKey /> Authentication</h2>
          <p>Include your API key in the request headers:</p>
          <div className="code--block">
            <pre>
              <code>X-API-Key: your_api_key_here</code>
            </pre>
          </div>
          <p className="info--box">
            💡 Generate an API key from your Settings page in the company dashboard.
          </p>
        </section>

        {/* Endpoints */}
        <section className="doc--section">
          <h2>📡 Endpoints</h2>

          {/* Create Order */}
          <div className="endpoint">
            <div className="endpoint--header">
              <span className="method post">POST</span>
              <span className="path">/orders</span>
            </div>
            <p>Create a new delivery order</p>

            <h4>Required Fields</h4>
            <ul className="field--list">
              <li><code>customerName</code> - Customer's full name</li>
              <li><code>customerPhone</code> - Customer's phone number</li>
              <li><code>pickupAddress</code> - Pickup location address</li>
              <li><code>deliveryAddress</code> - Delivery destination address</li>
            </ul>

            <h4>Optional Fields</h4>
            <ul className="field--list">
              <li><code>customerEmail</code> - Customer's email</li>
              <li><code>pickupLat</code>, <code>pickupLng</code> - Pickup coordinates</li>
              <li><code>deliveryLat</code>, <code>deliveryLng</code> - Delivery coordinates</li>
              <li><code>packageDescription</code> - Package details</li>
              <li><code>packageWeight</code> - Package weight</li>
              <li><code>packageValue</code> - Declared value</li>
              <li><code>deliveryFee</code> - Delivery fee (optional, auto-calculated if not provided)</li>
              <li><code>deliveryInstructions</code> - Special instructions</li>
            </ul>

            <h4>Response</h4>
            <div className="code--block">
              <pre>{`{
  "status": "Order created successfully",
  "data": {
    "trackingNumber": "PL1234567890ABCD",
    "orderId": "order-123456-abcdef",
    "status": "PENDING",
    "estimatedDeliveryTime": "2-4 hours",
    "trackingUrl": "https://astar.ng/track/PL1234567890ABCD"
  }
}`}</pre>
            </div>
          </div>

          {/* Get Order Status */}
          <div className="endpoint">
            <div className="endpoint--header">
              <span className="method get">GET</span>
              <span className="path">/orders/:trackingNumber</span>
            </div>
            <p>Retrieve the current status of a specific order</p>

            <h4>Response</h4>
            <div className="code--block">
              <pre>{`{
  "status": "Order fetched successfully",
  "data": {
    "trackingNumber": "PL1234567890ABCD",
    "status": "IN_TRANSIT",
    "customerName": "John Doe",
    "pickupAddress": "123 Pickup Street, Lagos",
    "deliveryAddress": "456 Delivery Avenue, Lagos",
    "packageDescription": "Electronics",
    "createdAt": 1234567890000,
    "updatedAt": 1234567900000,
    "riderInfo": {
      "riderId": "rider-123"
    }
  }
}`}</pre>
            </div>
          </div>

          {/* Get All Orders */}
          <div className="endpoint">
            <div className="endpoint--header">
              <span className="method get">GET</span>
              <span className="path">/orders?page=1&limit=20</span>
            </div>
            <p>Retrieve a paginated list of all your orders</p>

            <h4>Query Parameters</h4>
            <ul className="field--list">
              <li><code>page</code> - Page number (default: 1)</li>
              <li><code>limit</code> - Items per page (default: 20, max: 100)</li>
            </ul>
          </div>
        </section>

        {/* Order Status Values */}
        <section className="doc--section">
          <h2>📊 Order Status Values</h2>
          <div className="status--list">
            <div className="status--item">
              <span className="status--badge pending">PENDING</span>
              <span>Order created, awaiting rider assignment</span>
            </div>
            <div className="status--item">
              <span className="status--badge assigned">ASSIGNED</span>
              <span>Rider assigned to the order</span>
            </div>
            <div className="status--item">
              <span className="status--badge picked">PICKED_UP</span>
              <span>Package picked up from sender</span>
            </div>
            <div className="status--item">
              <span className="status--badge transit">IN_TRANSIT</span>
              <span>Package is on the way to destination</span>
            </div>
            <div className="status--item">
              <span className="status--badge delivered">DELIVERED</span>
              <span>Package successfully delivered</span>
            </div>
            <div className="status--item">
              <span className="status--badge cancelled">CANCELLED</span>
              <span>Order cancelled</span>
            </div>
          </div>
        </section>

        {/* Code Examples */}
        <section className="doc--section">
          <h2><FaCode /> Code Examples</h2>

          <div className="language--tabs">
            <button 
              className={`lang--tab ${activeLanguage === 'javascript' ? 'active' : ''}`}
              onClick={() => setActiveLanguage('javascript')}
            >
              JavaScript
            </button>
            <button 
              className={`lang--tab ${activeLanguage === 'php' ? 'active' : ''}`}
              onClick={() => setActiveLanguage('php')}
            >
              PHP
            </button>
            <button 
              className={`lang--tab ${activeLanguage === 'python' ? 'active' : ''}`}
              onClick={() => setActiveLanguage('python')}
            >
              Python
            </button>
          </div>

          <div className="code--example">
            <h4>Create Order</h4>
            <div className="code--block">
              <pre><code>{codeExamples[activeLanguage].createOrder}</code></pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(codeExamples[activeLanguage].createOrder, 'create')}
              >
                {copiedCode === 'create' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          </div>

          <div className="code--example">
            <h4>Get Order Status</h4>
            <div className="code--block">
              <pre><code>{codeExamples[activeLanguage].getStatus}</code></pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(codeExamples[activeLanguage].getStatus, 'status')}
              >
                {copiedCode === 'status' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="doc--section">
          <h2>✨ Best Practices</h2>
          <ol className="best--practices">
            <li>
              <strong>Secure Your API Key</strong>
              <p>Never expose your API key in client-side code. Store it in environment variables.</p>
            </li>
            <li>
              <strong>Error Handling</strong>
              <p>Always implement proper error handling and retry logic for transient failures.</p>
            </li>
            <li>
              <strong>Rate Limiting</strong>
              <p>Implement reasonable delays between requests and cache responses when appropriate.</p>
            </li>
            <li>
              <strong>Data Validation</strong>
              <p>Validate all data before sending to the API. Use proper phone number formatting.</p>
            </li>
          </ol>
        </section>

        {/* Support */}
        <section className="doc--section support--section">
          <h2>💬 Need Help?</h2>
          <p>Our support team is here to assist you with API integration and troubleshooting.</p>
          <div className="support--links">
            <a href="mailto:api-support@astarlogistics.com" className="support--link">
              📧 api-support@astarlogistics.com
            </a>
            <button onClick={() => navigate('/company/dashboard/settings')} className="support--link">
              ⚙️ Back to Settings
            </button>
          </div>
        </section>
      </div>
      ) : (
        /* Embed Widget Documentation */
        <div className="doc--content">
          <section className="doc--section">
            <h2>🎯 Embeddable Tracking Widget</h2>
            <p>
              Embed real-time order tracking directly into your website with a simple iframe. 
              Customize colors, features, and refresh intervals to match your brand.
            </p>
          </section>

          {/* Basic Usage */}
          <section className="doc--section">
            <h2>🚀 Basic Usage</h2>
            <p>Add this code to any webpage to embed order tracking:</p>
            <div className="code--block">
              <pre>{`<iframe 
  src="https://astar.ng/embed/track/PKPMS7E5661VALQ"
  width="100%" 
  height="600px" 
  frameborder="0"
  style="border: none; border-radius: 8px;"
></iframe>`}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`<iframe src="https://astar.ng/embed/track/PKPMS7E5661VALQ" width="100%" height="600px" frameborder="0" style="border: none; border-radius: 8px;"></iframe>`, 'embed-basic')}
              >
                {copiedCode === 'embed-basic' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
            <p className="info--box">
              💡 Replace <code>PKPMS7E5661VALQ</code> with the actual tracking number from your order.
            </p>
          </section>

          {/* Live Preview */}
          <section className="doc--section">
            <h2>👁️ Live Preview</h2>
            <p>Example of what the embedded tracking looks like:</p>
            <div className="embed--preview">
              <iframe 
                src="https://astar.ng/embed/track/PKPMS7E5661VALQ?primaryColor=%23FE7D11&mapStyle=default"
                width="100%" 
                height="500px" 
                frameBorder="0"
                style={{ border: 'none', borderRadius: '8px' }}
                title="Tracking Preview"
              />
            </div>
          </section>

          {/* Customization Options */}
          <section className="doc--section">
            <h2>🎨 Customization Options</h2>
            <p>Add URL parameters to customize the widget:</p>

            <h3>Color Customization</h3>
            <table className="params--table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>primaryColor</code></td>
                  <td>#FE7D11</td>
                  <td>Primary accent color (route line)</td>
                </tr>
                <tr>
                  <td><code>pickupColor</code></td>
                  <td>#4CAF50</td>
                  <td>Pickup marker color</td>
                </tr>
                <tr>
                  <td><code>deliveryColor</code></td>
                  <td>#FE7D11</td>
                  <td>Delivery marker color</td>
                </tr>
                <tr>
                  <td><code>riderColor</code></td>
                  <td>#2196F3</td>
                  <td>Rider marker color</td>
                </tr>
              </tbody>
            </table>

            <h4>Example - Custom Colors</h4>
            <div className="code--block">
              <pre>{`<iframe 
  src="https://astar.ng/embed/track/PKPMS7E5661VALQ?primaryColor=%232E7D32&pickupColor=%23FF5722&deliveryColor=%239C27B0&riderColor=%23FF9800"
  width="100%" 
  height="600px"
></iframe>`}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`<iframe src="https://astar.ng/embed/track/PKPMS7E5661VALQ?primaryColor=%232E7D32&pickupColor=%23FF5722&deliveryColor=%239C27B0&riderColor=%23FF9800" width="100%" height="600px"></iframe>`, 'embed-colors')}
              >
                {copiedCode === 'embed-colors' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
            <p className="info--box">
              ⚠️ <strong>Important:</strong> URL encode color values (# becomes %23)
            </p>

            <h3>Feature Toggles</h3>
            <table className="params--table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>showLegend</code></td>
                  <td>true</td>
                  <td>Show/hide map legend</td>
                </tr>
                <tr>
                  <td><code>showRoute</code></td>
                  <td>true</td>
                  <td>Show/hide route polyline</td>
                </tr>
                <tr>
                  <td><code>autoRefresh</code></td>
                  <td>true</td>
                  <td>Enable/disable auto-refresh</td>
                </tr>
                <tr>
                  <td><code>refreshInterval</code></td>
                  <td>15000</td>
                  <td>Auto-refresh interval (milliseconds)</td>
                </tr>
              </tbody>
            </table>

            <h4>Example - Disable Legend & Auto-refresh</h4>
            <div className="code--block">
              <pre>{`<iframe 
  src="https://astar.ng/embed/track/PKPMS7E5661VALQ?showLegend=false&autoRefresh=false"
  width="100%" 
  height="600px"
></iframe>`}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`<iframe src="https://astar.ng/embed/track/PKPMS7E5661VALQ?showLegend=false&autoRefresh=false" width="100%" height="600px"></iframe>`, 'embed-features')}
              >
                {copiedCode === 'embed-features' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>

            <h3>Map Styles</h3>
            <table className="params--table">
              <thead>
                <tr>
                  <th>Value</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>default</code></td>
                  <td>Standard OpenStreetMap style</td>
                </tr>
                <tr>
                  <td><code>dark</code></td>
                  <td>Dark mode map (CARTO dark theme)</td>
                </tr>
                <tr>
                  <td><code>satellite</code></td>
                  <td>Satellite imagery (Esri World Imagery)</td>
                </tr>
              </tbody>
            </table>

            <h4>Example - Dark Mode Map</h4>
            <div className="code--block">
              <pre>{`<iframe 
  src="https://astar.ng/embed/track/PKPMS7E5661VALQ?mapStyle=dark"
  width="100%" 
  height="600px"
></iframe>`}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`<iframe src="https://astar.ng/embed/track/PKPMS7E5661VALQ?mapStyle=dark" width="100%" height="600px"></iframe>`, 'embed-dark')}
              >
                {copiedCode === 'embed-dark' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          </section>

          {/* Advanced Examples */}
          <section className="doc--section">
            <h2>🔥 Complete Example</h2>
            <p>All customizations combined:</p>
            <div className="code--block">
              <pre>{`<iframe 
  src="https://astar.ng/embed/track/PKPMS7E5661VALQ?primaryColor=%23FF6B6B&pickupColor=%234ECDC4&deliveryColor=%23FFE66D&riderColor=%2395E1D3&mapStyle=dark&showLegend=true&autoRefresh=true&refreshInterval=10000"
  width="100%" 
  height="700px" 
  frameborder="0"
  style="border: none; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"
></iframe>`}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`<iframe src="https://astar.ng/embed/track/PKPMS7E5661VALQ?primaryColor=%23FF6B6B&pickupColor=%234ECDC4&deliveryColor=%23FFE66D&riderColor=%2395E1D3&mapStyle=dark&showLegend=true&autoRefresh=true&refreshInterval=10000" width="100%" height="700px" frameborder="0" style="border: none; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"></iframe>`, 'embed-complete')}
              >
                {copiedCode === 'embed-complete' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          </section>

          {/* Responsive Design */}
          <section className="doc--section">
            <h2>📱 Responsive Design</h2>
            <p>Make your embed responsive with this wrapper:</p>
            <div className="code--block">
              <pre>{`<div style="position: relative; padding-bottom: 75%; height: 0; overflow: hidden;">
  <iframe 
    src="https://astar.ng/embed/track/PKPMS7E5661VALQ"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
  ></iframe>
</div>`}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`<div style="position: relative; padding-bottom: 75%; height: 0; overflow: hidden;"><iframe src="https://astar.ng/embed/track/PKPMS7E5661VALQ" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"></iframe></div>`, 'embed-responsive')}
              >
                {copiedCode === 'embed-responsive' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          </section>

          {/* JavaScript Integration */}
          <section className="doc--section">
            <h2>💻 JavaScript Integration</h2>
            <p>Dynamically generate embed URLs:</p>
            <div className="code--block">
              <pre>{`function generateTrackingEmbed(trackingNumber, options = {}) {
  const baseUrl = 'https://astar.ng/embed/track/'
  const params = new URLSearchParams()
  
  if (options.primaryColor) params.append('primaryColor', options.primaryColor)
  if (options.mapStyle) params.append('mapStyle', options.mapStyle)
  if (options.refreshInterval) params.append('refreshInterval', options.refreshInterval)
  
  const query = params.toString()
  return \`\${baseUrl}\${trackingNumber}\${query ? '?' + query : ''}\`
}

// Usage
const embedUrl = generateTrackingEmbed('PKPMS7E5661VALQ', {
  primaryColor: '#FF6B6B',
  mapStyle: 'dark',
  refreshInterval: 10000
})

document.getElementById('tracking-container').innerHTML = \`
  <iframe src="\${embedUrl}" width="100%" height="600px"></iframe>
\``}</pre>
              <button 
                className="copy--btn"
                onClick={() => copyToClipboard(`function generateTrackingEmbed(trackingNumber, options = {}) {\n  const baseUrl = 'https://astar.ng/embed/track/'\n  const params = new URLSearchParams()\n  \n  if (options.primaryColor) params.append('primaryColor', options.primaryColor)\n  if (options.mapStyle) params.append('mapStyle', options.mapStyle)\n  if (options.refreshInterval) params.append('refreshInterval', options.refreshInterval)\n  \n  const query = params.toString()\n  return \`\${baseUrl}\${trackingNumber}\${query ? '?' + query : ''}\`\n}\n\nconst embedUrl = generateTrackingEmbed('PKPMS7E5661VALQ', {\n  primaryColor: '#FF6B6B',\n  mapStyle: 'dark',\n  refreshInterval: 10000\n})`, 'embed-js')}
              >
                {copiedCode === 'embed-js' ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          </section>

          {/* Features */}
          <section className="doc--section">
            <h2>✨ Features</h2>
            <ul className="feature--list">
              <li>🗺️ <strong>Real-time Updates:</strong> Auto-refreshes rider location every 15 seconds (configurable)</li>
              <li>📍 <strong>Interactive Map:</strong> Fully interactive with zoom, pan, and marker popups</li>
              <li>🛣️ <strong>Route Visualization:</strong> Shows actual road routes using OSRM routing engine</li>
              <li>📱 <strong>Responsive:</strong> Works perfectly on all screen sizes and devices</li>
              <li>🎨 <strong>Customizable:</strong> Colors, map styles, and features can be configured</li>
              <li>⚡ <strong>Lightweight:</strong> Minimal interface optimized for embedding</li>
            </ul>
          </section>

          {/* Performance Tips */}
          <section className="doc--section">
            <h2>⚡ Performance Tips</h2>
            <ol className="best--practices">
              <li>
                <strong>Refresh Interval</strong>
                <p>Set appropriate <code>refreshInterval</code> based on your needs (higher = less server load)</p>
              </li>
              <li>
                <strong>Disable Auto-refresh</strong>
                <p>Set <code>autoRefresh=false</code> for completed deliveries to reduce unnecessary API calls</p>
              </li>
              <li>
                <strong>Simplify Map</strong>
                <p>Use <code>showRoute=false</code> for simpler maps with better performance</p>
              </li>
              <li>
                <strong>Lazy Loading</strong>
                <p>Consider lazy-loading iframes on pages with multiple embeds</p>
              </li>
            </ol>
          </section>

          {/* Support */}
          <section className="doc--section support--section">
            <h2>💬 Need Help?</h2>
            <p>Our team is here to help you integrate the tracking widget into your website.</p>
            <div className="support--links">
              <a href="mailto:api-support@astarlogistics.com" className="support--link">
                📧 api-support@astarlogistics.com
              </a>
              <button onClick={() => navigate('/company/dashboard/settings')} className="support--link">
                ⚙️ Back to Settings
              </button>
            </div>
          </section>
        </div>
      )}
    </Container>
  );
};

export default ApiDocumentation;
